'use client';

import * as React from 'react';
import {
  FileText,
  Folder,
  Mail,
  MessageSquare,
  Newspaper,
  Settings,
  Video,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Link from '@/compat/link';
import { usePathname } from '@/compat/navigation';
import { useSpace } from '@/contexts/space-context';
import { SpaceRole } from '@/lib/types';

const iconNavItems = [
  { icon: Newspaper, label: 'Feed', path: 'feed' },
  { icon: Folder, label: 'Projects', path: 'overview' },
  { icon: FileText, label: 'Wiki', path: 'wiki' },
  { icon: Mail, label: 'Mail', path: 'mail' },
  { icon: MessageSquare, label: 'Chat', path: 'chat' },
  { icon: Video, label: 'Meetings', path: 'meetings' },
];

const bottomNavItems = [
  { icon: Settings, label: 'Settings', path: 'settings' },
];

export function IconSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const pathname = usePathname();
  const { space, membership } = useSpace();
  const isOwner = membership.role === SpaceRole.Owner;

  const isActive = (path: string) => {
    if (path === 'overview') {
      return pathname.includes('/spaces/') &&
        !pathname.includes('/wiki') &&
        !pathname.includes('/mail') &&
        !pathname.includes('/feed') &&
        !pathname.includes('/chat') &&
        !pathname.includes('/meetings') &&
        !pathname.includes('/settings');
    }
    return pathname.includes(`/${path}`);
  };

  const getHref = (path: string) => `/spaces/${space.slug}/${path}`;

  const visibleBottomNavItems = bottomNavItems.filter((item) => {
    if (item.path === 'settings') return isOwner;
    return true;
  });

  return (
    <Sidebar
      collapsible="none"
      className="!w-[60px] bg-muted"
      {...props}
    >
      <SidebarContent className="py-2 flex flex-col justify-center">
        <TooltipProvider delayDuration={0}>
          <SidebarMenu>
            {iconNavItems.map((item) => (
              <SidebarMenuItem key={item.label} className="px-3 py-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton
                      asChild
                      className={`flex items-center justify-center h-auto py-2 ${isActive(item.path) ? 'bg-accent text-accent-foreground' : ''}`}
                    >
                      <Link href={getHref(item.path)}>
                        <item.icon className="h-5 w-5" />
                      </Link>
                    </SidebarMenuButton>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </TooltipProvider>
      </SidebarContent>
      <SidebarFooter className="py-2">
        <TooltipProvider delayDuration={0}>
          <SidebarMenu>
            {visibleBottomNavItems.map((item) => (
              <SidebarMenuItem key={item.label} className="px-3 py-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton
                      asChild
                      className={`flex items-center justify-center h-auto py-2 ${isActive(item.path) ? 'bg-accent text-accent-foreground' : ''}`}
                    >
                      <Link href={getHref(item.path)}>
                        <item.icon className="h-5 w-5" />
                      </Link>
                    </SidebarMenuButton>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </TooltipProvider>
      </SidebarFooter>
    </Sidebar>
  );
}
